{
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "$id": "https://raw.githubusercontent.com/jwildfire/safety.viz/main/src/data/schema/shift-plot.json",
  "title": "safety.viz shift-plot data contract",
  "description": "Long-format results data: one record per participant per visit per measure (SSP-DATA-001). Column names are supplied by the settings mapping; the shift plot pairs each participant's baseline-visit value against their comparison-visit value for the selected measure, removes missing/non-numeric results with a reported count (SSP-REG-020), and degrades gracefully when optional columns are absent (SSP-DATA-003).",
  "type": "object",
  "required": ["data", "settings"],
  "properties": {
    "data": {
      "type": "array",
      "minItems": 1,
      "items": { "type": "object" },
      "description": "d3.csv()-style records; every row carries the measure, visit, and result columns named in settings."
    },
    "settings": {
      "type": "object",
      "description": "Column mappings and rendering options; merged onto the module's DEFAULT_SETTINGS, so only overrides need to be supplied.",
      "required": ["measure_col", "value_col", "visit_col"],
      "properties": {
        "measure_col": {
          "type": "string",
          "default": "TEST",
          "description": "Column holding the measure name; required in data."
        },
        "value_col": {
          "type": "string",
          "default": "STRESN",
          "description": "Column holding the numeric result; required in data."
        },
        "visit_col": {
          "type": "string",
          "default": "VISIT",
          "description": "Column holding the visit label; required in data. Its distinct values populate the baseline and comparison visit controls."
        },
        "visit_order_col": {
          "type": "string",
          "default": "VISITNUM",
          "description": "Optional numeric column that orders the visits; when absent the visits sort alphanumerically (SSP-REG-013/014)."
        },
        "id_col": {
          "type": "string",
          "default": "USUBJID",
          "description": "Optional participant identifier column; drives the participant counts and the default listing's first column."
        },
        "unit_col": {
          "type": "string",
          "default": "STRESU",
          "description": "Optional unit column, appended to measure labels."
        },
        "baseline_visits": {
          "$ref": "#/$defs/visitList",
          "description": "Optional baseline visit(s) selected on first render; defaults to the first visit (SSP-CFG-004)."
        },
        "comparison_visits": {
          "$ref": "#/$defs/visitList",
          "description": "Optional comparison visit(s) selected on first render; defaults to every visit after the baseline (SSP-CFG-005)."
        },
        "baseline_stat": {
          "type": "string",
          "enum": ["mean", "min", "max", "first"],
          "default": "mean",
          "description": "Summary statistic applied when a participant has several results across the baseline visit(s)."
        },
        "comparison_stat": {
          "type": "string",
          "enum": ["mean", "min", "max", "first"],
          "default": "mean",
          "description": "Summary statistic applied when a participant has several results across the comparison visit(s)."
        },
        "measures": {
          "type": ["array", "null"],
          "items": { "type": "string" },
          "default": null,
          "description": "Ordered whitelist of measures the Measure control offers: only these appear, and in this order. null or [] offers every measure in the data, alphabetically. Configured measures absent from the data are dropped with a console warning (SSP-MEAS-001)."
        },
        "axis_type": {
          "type": "string",
          "enum": ["linear", "log"],
          "default": "linear",
          "description": "Initial scale for both axes; the Axis Type control switches them together because the two axes share one domain. On the log scale a participant pair with a zero or negative value is removed and counted in the note above the chart (SSP-SCALE-001, SSP-SCALE-003)."
        },
        "filters": {
          "$ref": "#/$defs/filterList",
          "description": "Optional filter columns rendered as controls (SSP-CFG-006). Each entry is a column name or a { value_col, label, start, all, multiple } spec: `start` is the value the filter opens on, `all` (default true) is whether an \"All\" option is offered, and `multiple` (default false) makes it a checkbox multiselect."
        },
        "details": {
          "$ref": "#/$defs/fieldList",
          "description": "Optional listing columns; defaults to participant ID, baseline, comparison, change, and percent change (SSP-REQ-005)."
        }
      }
    }
  },
  "$defs": {
    "visitList": {
      "type": "array",
      "items": { "type": "string" }
    },
    "fieldList": {
      "type": "array",
      "items": {
        "anyOf": [
          { "type": "string" },
          {
            "type": "object",
            "required": ["value_col"],
            "properties": {
              "value_col": { "type": "string" },
              "label": { "type": "string" }
            }
          }
        ]
      }
    },
    "filterList": {
      "type": "array",
      "items": {
        "anyOf": [
          { "type": "string" },
          {
            "type": "object",
            "required": ["value_col"],
            "properties": {
              "value_col": { "type": "string" },
              "label": { "type": "string" },
              "start": {
                "type": ["string", "number", "boolean", "array", "null"],
                "default": null,
                "description": "The value the filter opens on; an array of values for a `multiple` filter. 0 and false are real values, not an absent start. A value the data lacks is dropped with a console warning."
              },
              "all": {
                "type": "boolean",
                "default": true,
                "description": "Whether the filter offers an \"All\" option. false removes it, and the filter then always holds one value: the first, unless `start` names another."
              },
              "multiple": {
                "type": "boolean",
                "default": false,
                "description": "Render a checkbox multiselect instead of a dropdown; the rows shown are those matching any selected value."
              }
            }
          }
        ]
      }
    }
  }
}
